import React from 'react';
import { useQuery } from '@apollo/client';
import { GET_SERVICE_TYPES } from '../../graphql/queries';
import { generateTimeOptions } from './utils/timeUtils';

/**
 * Job form fields component for basic job information
 * @param {Object} props - Component props
 * @param {Object} props.formData - Current form data
 * @param {Function} props.onChange - Form data change handler
 * @param {Object} props.errors - Validation errors
 * @returns {JSX.Element} JobFormFields component
 */
export default function JobFormFields({ formData, onChange, errors = {} }) {
  const { data: serviceTypesData, loading: serviceTypesLoading } = useQuery(GET_SERVICE_TYPES);

  const timeOptions = generateTimeOptions();

  const handleChange = (field, value) => {
    onChange({
      ...formData,
      [field]: value
    });
  };

  return (
    <>
      {/* Service Type Dropdown */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Service Type *
        </label>
        <select
          value={formData.serviceTypeId || ''}
          onChange={(e) => handleChange('serviceTypeId', e.target.value)}
          className={`w-full p-2 border rounded-md ${errors.serviceTypeId ? 'border-red-500' : ''}`}
          disabled={serviceTypesLoading}
          required
        >
          <option value="">Select Service Type</option>
          {serviceTypesData?.serviceTypes
            ?.filter(type => type.isActive)
            ?.map(type => (
              <option key={type.id} value={type.id}>
                {type.title}
              </option>
            ))}
        </select>
        {errors.serviceTypeId && (
          <p className="mt-1 text-xs text-red-500">{errors.serviceTypeId}</p>
        )}
        {formData.serviceTypeId && serviceTypesData && (
          <div className="mt-2 text-xs text-gray-500">
            Job will be created with category: {
              serviceTypesData.serviceTypes.find(type => type.id === formData.serviceTypeId)?.category || 'Unknown'
            }
          </div>
        )}
      </div>
      
      {/* Date & Time Selection */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Date *
          </label>
          <input
            type="date"
            value={formData.scheduledDate || ''}
            onChange={(e) => handleChange('scheduledDate', e.target.value)}
            className={`w-full p-2 border rounded-md ${errors.scheduledDate ? 'border-red-500' : ''}`}
            required
          />
          {errors.scheduledDate && (
            <p className="mt-1 text-xs text-red-500">{errors.scheduledDate}</p>
          )}
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Start Time
          </label>
          <select
            value={formData.startTime || '9:00 AM'}
            onChange={(e) => handleChange('startTime', e.target.value)}
            className="w-full p-2 border rounded-md"
          >
            {timeOptions.map(time => (
              <option key={time} value={time}>{time}</option>
            ))}
          </select>
        </div>
      </div>
      
      {/* Notes */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Notes
        </label>
        <textarea
          value={formData.notes || ''}
          onChange={(e) => handleChange('notes', e.target.value)}
          className="w-full p-2 border rounded-md h-24"
          placeholder="Add notes about this job..."
        />
      </div>
    </>
  );
} 